import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

interface CookieBannerProps {
  message?: string;
  acceptText?: string;
  declineText?: string;
  className?: string;
  position?: 'bottom' | 'top';
}

const EXIT_MS = 300;

const CookieBanner = (props: CookieBannerProps) => {
  const {
    message = 'We use cookies to improve your experience. By using our site, you accept cookies.',
    acceptText = 'Accept',
    declineText = 'Decline',
    className,
    position = 'bottom',
  } = props;

  const [visible, setVisible] = useState(false);
  const [render, setRender] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('cookie-consent');
    if (!stored) {
      setRender(true);
      requestAnimationFrame(() => setVisible(true));
    }
  }, []);

  const closeWithExit = () => {
    setVisible(false);
    setTimeout(() => setRender(false), EXIT_MS);
  };

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'true');
    closeWithExit();
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'false');
    closeWithExit();
  };

  if (!render) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className={cn(
        'fixed left-1/2 z-50 w-[95%] max-w-lg -translate-x-1/2',
        position === 'top' ? 'top-4' : 'bottom-4'
      )}
    >
      <div
        className={cn(
          'rounded-lg bg-slate-800 text-white shadow-lg border border-slate-700',
          'p-4 flex flex-col sm:flex-row items-center gap-3',
          'transition-all duration-300 ease-out',
          visible
            ? 'opacity-100 translate-y-0'
            : position === 'bottom'
              ? 'opacity-0 translate-y-4'
              : 'opacity-0 -translate-y-4',
          className
        )}
      >
        <p className="text-sm flex-1 text-slate-200">{message}</p>

        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={handleDecline}
            className={cn(
              'cursor-pointer px-3 py-1.5 rounded-md border border-slate-600',
              'bg-slate-700 text-slate-300 text-sm',
              'transition-colors duration-200 hover:bg-slate-600'
            )}
          >
            {declineText}
          </button>

          <button
            type="button"
            onClick={handleAccept}
            className={cn(
              'cursor-pointer px-3 py-1.5 rounded-md',
              'bg-white text-slate-800 text-sm font-medium',
              'transition-colors duration-200 hover:bg-slate-100'
            )}
          >
            {acceptText}
          </button>
        </div>
      </div>
    </div>
  );
};

export { CookieBanner };
